import { repairImageKitUrl } from '@dupe/common/constants'
import { ChevronRight, ImageOff } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { cn } from '~/src/lib/utils'
import type { HubHeroSection, HubSectionComponentProps } from './types'

const ASPECT_CLASS: Record<
  NonNullable<HubHeroSection['heroImage']>['aspect'] & string,
  string
> = {
  editorial: 'aspect-[16/9]',
  wide: 'aspect-[21/9]',
  square: 'aspect-square',
}

const GENERATED_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
})

function formatGeneratedDate(value: string | null | undefined): string | null {
  if (!value) return null
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  const date = dateMatch
    ? new Date(
        Date.UTC(
          Number(dateMatch[1]),
          Number(dateMatch[2]) - 1,
          Number(dateMatch[3])
        )
      )
    : new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return GENERATED_DATE_FORMATTER.format(date)
}

function canUseNextImage(url: string): boolean {
  if (url.startsWith('/')) return true
  try {
    const host = new URL(url).hostname
    return host === 'ik.imagekit.io' || host === 'assets.dupe.com'
  } catch {
    return false
  }
}

function HeroImage({
  url,
  alt,
  aspect,
}: {
  url: string
  alt: string
  aspect: NonNullable<HubHeroSection['heroImage']>['aspect']
}) {
  const [errored, setErrored] = useState(false)
  const aspectClass = ASPECT_CLASS[aspect ?? 'editorial']
  const heroUrl = repairImageKitUrl(url)
  const useNext = canUseNextImage(heroUrl)

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-2xl bg-ds-bg-secondary',
        aspectClass
      )}
    >
      {errored ? (
        <div className='flex h-full w-full items-center justify-center'>
          <ImageOff
            className='size-10 text-foreground-muted'
            strokeWidth={1.5}
          />
        </div>
      ) : useNext ? (
        <Image
          src={heroUrl}
          alt={alt}
          fill
          priority
          sizes='(max-width: 768px) 100vw, 1024px'
          className='object-cover'
          onError={() => setErrored(true)}
        />
      ) : (
        <img
          src={heroUrl}
          alt={alt}
          className='absolute inset-0 h-full w-full object-cover'
          fetchPriority='high'
          onError={() => setErrored(true)}
        />
      )}
    </div>
  )
}

export function HubHero({
  section,
  context,
}: HubSectionComponentProps<HubHeroSection>) {
  const { kicker, breadcrumbs, title, dek, byline, metaLine, heroImage } =
    section
  const pageMeta = context.pageMeta
  const displayByline = pageMeta?.author ?? byline
  const generatedDate = formatGeneratedDate(pageMeta?.lastGeneratedAt)
  const sourceCount = pageMeta?.sourceCount
  const reportCount = pageMeta?.reportCount
  const shouldRenderDeterministicMeta =
    Boolean(generatedDate) ||
    Boolean(sourceCount && sourceCount > 0) ||
    Boolean(reportCount && reportCount > 0)

  return (
    <header id={section.id} className='flex flex-col gap-5'>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label='Breadcrumb'>
          <ol className='flex flex-wrap items-center gap-1.5'>
            {breadcrumbs.map((crumb, i) => (
              <li
                key={crumb.href ?? crumb.label}
                className='flex items-center gap-1.5'
              >
                {i > 0 && (
                  <ChevronRight
                    aria-hidden
                    className='size-4 text-muted-foreground'
                  />
                )}
                {crumb.href ? (
                  <Link
                    href={crumb.href}
                    className='text-sm text-ds-text-quaternary hover:text-ds-text-secondary'
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span
                    aria-current='page'
                    className='line-clamp-1 text-sm text-ds-text-quaternary'
                  >
                    {crumb.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {kicker && (
        <span className='inline-flex w-fit items-center rounded-full bg-success-50 px-3 py-1 text-xs font-medium text-success-700'>
          {kicker}
        </span>
      )}

      <div className='flex flex-col gap-3'>
        <h1 className='text-balance font-display text-[30px] font-semibold leading-[36px] tracking-[-0.6px] text-foreground md:text-4xl md:leading-[1.1] lg:text-5xl'>
          {title}
        </h1>
        {dek && (
          <p className='max-w-[60ch] text-base leading-6 text-foreground-secondary'>
            {dek}
          </p>
        )}
        {shouldRenderDeterministicMeta ? (
          <p className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground-secondary'>
            {generatedDate && <span>Updated {generatedDate}</span>}
            {reportCount && reportCount > 0 && (
              <>
                {generatedDate && <span aria-hidden>·</span>}
                <span>{reportCount.toLocaleString('en-US')} reports</span>
              </>
            )}
            {sourceCount && sourceCount > 0 && (
              <>
                {(generatedDate || Boolean(reportCount && reportCount > 0)) && (
                  <span aria-hidden>·</span>
                )}
                {pageMeta?.sourceHref ? (
                  <Link
                    href={pageMeta.sourceHref}
                    className='font-medium text-info-700 underline-offset-4 hover:underline'
                  >
                    {sourceCount.toLocaleString('en-US')} sources
                  </Link>
                ) : (
                  <span>{sourceCount.toLocaleString('en-US')} sources</span>
                )}
              </>
            )}
          </p>
        ) : (
          metaLine && (
            <p className='text-sm text-foreground-secondary'>{metaLine}</p>
          )
        )}
      </div>

      {displayByline && (
        <aside className='flex items-start gap-4'>
          <div className='flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-accent md:h-14 md:w-14'>
            {displayByline.avatarUrl ? (
              <img
                src={displayByline.avatarUrl}
                alt={displayByline.name}
                className='h-12 w-12 object-cover md:h-14 md:w-14'
              />
            ) : (
              <span className='text-base font-semibold text-foreground-secondary'>
                {displayByline.name.charAt(0)}
              </span>
            )}
          </div>
          <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
            <p className='text-sm text-foreground'>
              By <span className='font-semibold'>{displayByline.name}</span>
            </p>
            {displayByline.role && (
              <p className='text-sm leading-5 text-foreground-secondary md:text-base md:leading-6'>
                {displayByline.role}
              </p>
            )}
          </div>
        </aside>
      )}

      {heroImage && (
        <HeroImage
          url={heroImage.url}
          alt={heroImage.alt}
          aspect={heroImage.aspect ?? 'editorial'}
        />
      )}
    </header>
  )
}
